<template>
  <div class="completion-step">
    <div class="completion-content">
      <!-- Main content block -->
      <div class="main-content-block">
        <!-- Image -->
        <div class="image-container">
          <img :src="avatarImage" alt="AI Assistant" class="completion-image" />
        </div>

        <!-- Categories -->
        <div class="categories-section">
          <h1 class="step-title">{{ step.title }}</h1>
          <div class="text-section">
            <p class="completion-text">
              Спасибо! Теперь мы лучше понимаем, что может понравиться вашему близкому.
            </p>
            <p class="completion-text">
              На основе ваших ответов мы собрали список подходящих подарков — он уже ждёт вас.
            </p>
          </div>
        </div>
      </div>

      <!-- Bottom block -->
      <div class="bottom-block">
        <!-- Loading Indicator -->
        <div v-if="giftStore.giftModal.isLoading" class="loading-section">
          <div class="loading-spinner"></div>
          <p>Формируем подборку...</p>
        </div>

        <!-- Action Button -->
        <custom-button
          v-else
          @click="handleAction"
          class="action-button"
          rounded
        >
          Перейти к подборке
        </custom-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import CustomButton from '@/components/UI/CustomButtom/CustomButton.vue'
import { useGiftModal } from '@/features/gift/store'
import type { GiftStep } from '@/features/gift/types/gift-config'

interface Props {
  step: GiftStep & { type: 'completion' }
}

const props = defineProps<Props>()
const giftStore = useGiftModal()

const avatarImage = '/img/survey-avatar.png'

async function handleAction() {
  // Экшн будет добавлен пользователем позже
  // Здесь можно использовать giftStore.giftModal.resultLink для навигации
}

onMounted(async () => {
  // Отправляем данные на бекенд при переходе на этот шаг
  try {
    await giftStore.submitGiftSurvey()
  } catch (error) {
    console.error('Error submitting gift survey:', error)
  }
})
</script>

<style scoped>
.completion-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  height: 100%;
}

.completion-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 500px;
  height: 100%;
  width: 100%;
}

.main-content-block {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.image-container {
  display: flex;
  justify-content: center;
  margin-bottom: 32px;
}

.completion-image {
  width: 330px;
  height: 330px;
  border-radius: 50%;
  object-fit: cover;
}

.categories-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.step-title {
  font-family: 'Gilroy';
  font-size: 32px;
  font-weight: 700;
  color: var(--primary-blue-100);
  margin: 0;
  line-height: 1.09;
  letter-spacing: -0.64px;
  text-align: left;
}

.text-section {
  margin-bottom: 0;
  text-align: left;
}

.completion-text {
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0 0 16px 0;
}

.bottom-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 20px;
}

.action-button {
  width: 100%;
  min-height: 56px;
}

.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-section p {
  font-size: 14px;
  margin: 0;
  font-weight: 500;
}

@media (min-width: 1919px) {
  .completion-content {
    padding: 40px 120px;
  }
}

@media (min-width: 1024px) {
  .completion-step {
    padding: 0;
  }

  .completion-content {
    padding: 40px;
    gap: 40px;
    max-width: 100%;
    justify-content: flex-start;
  }

  .main-content-block {
    flex-direction: row;
    align-items: center;
    gap: 40px;
    width: 100%;
  }

  .image-container {
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .completion-image {
    width: 234px;
    height: 234px;
    border-radius: 20px;
    object-fit: cover;
  }

  .categories-section {
    gap: 8px;
    flex: 1;
  }

  .step-title {
    font-family: 'Gilroy', sans-serif;
    font-size: 48px;
    font-weight: 700;
    line-height: 1.2;
    color: var(--primary-blue-100);
    letter-spacing: -0.04em;
  }

  .text-section {
    margin-bottom: 0;
  }

  .completion-text {
    font-family: 'Inter', sans-serif;
    font-size: 20px;
    font-weight: 400;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
    letter-spacing: -0.04em;
  }

  .bottom-block {
    align-items: flex-end;
    padding: 20px 0 0 0;
  }

  .action-button {
    width: auto;
  }
}

@media (max-width: 1023px) {
  .completion-step {
    padding: 0;
  }

  .completion-content {
    padding: 20px 32px;
    gap: 40px;
    max-width: 100%;
  }

  .image-container {
    margin-bottom: 0;
    width: 100%;
  }

  .completion-image {
    height: 329px;
    border-radius: 20px;
    object-fit: cover;
  }

  .categories-section {
    gap: 20px;
  }

  .step-title {
    font-family: 'Gilroy', sans-serif;
    font-size: 32px;
    font-weight: 700;
    line-height: 1.09;
    color: #232F45;
    letter-spacing: -0.02em;
  }

  .text-section {
    gap: 20px;
  }

  .completion-text {
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.3;
    color: rgba(35, 47, 69, 0.8);
    letter-spacing: -0.06em;
  }
}

@media (max-width: 480px) {
  .completion-content {
    padding: 20px;
  }
}
</style>

